import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Search, Wrench } from "lucide-react";
import { api, money, shopDateTime, shopToday } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatPhone } from "@/lib/phone";

export const REPAIR_STATUS = {
  booked_in: { label: "Booked in", cls: "bg-[#0A0A0A] text-white" },
  in_progress: { label: "In progress", cls: "bg-[#C5A059] text-[#0A0A0A]" },
  ready: { label: "Ready for collection", cls: "bg-[#166534] text-white" },
  collected: { label: "Collected", cls: "bg-muted text-foreground" },
  on_hold: { label: "On hold", cls: "bg-[#9A3412] text-white" },
  cancelled: { label: "Cancelled", cls: "bg-neutral-400 text-white" },
};

const FILTERS = [
  { key: "open", label: "Open jobs" },
  { key: "booked_in", label: "Booked in" },
  { key: "in_progress", label: "In progress" },
  { key: "ready", label: "Ready" },
  { key: "collected", label: "Collected" },
  { key: "", label: "Everything" },
];

export default function Repairs() {
  const { branch } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState("open");
  const [overdue, setOverdue] = useState(false);
  const [allBranches, setAllBranches] = useState(false);
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/repairs", {
        params: {
          ...(status ? { status } : {}),
          ...(overdue ? { overdue: true } : {}),
          ...(allBranches ? {} : { branch }),
          ...(q.trim() ? { q: q.trim() } : {}),
        },
      })
      .then(({ data }) => setRows(data))
      .catch(() => toast.error("Could not load the repairs board"))
      .finally(() => setLoading(false));
  }, [status, overdue, allBranches, branch, q]);

  // Melbourne's today, not the browser's. toISOString() would make it yesterday until
  // 11am, which marked repairs overdue a day early every morning.
  const today = shopToday();

  return (
    <div className="fade-up" data-testid="repairs-board">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[#B38F4E]">
            {allBranches ? "All branches" : branch}
          </p>
          <h1 className="mt-2 text-3xl font-bold sm:text-4xl">Repairs</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Every job from drop-off to collection, with photos, notes and who took it home.
          </p>
        </div>
        <Button
          className="rounded-none bg-[#C5A059] font-semibold text-[#0A0A0A] hover:bg-[#B38F4E]"
          onClick={() => navigate("/repairs/new")}
          data-testid="new-repair-button"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Book in a repair
        </Button>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="repair-filters">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            type="button"
            onClick={() => setStatus(f.key)}
            className={`border px-4 py-2 text-sm transition-colors duration-150 ${
              status === f.key
                ? "border-[#C5A059] bg-[#FBF8F2] font-semibold"
                : "border-border hover:bg-muted"
            }`}
            data-testid={`repair-filter-${f.key || "all"}`}
          >
            {f.label}
          </button>
        ))}
        <label className="flex items-center gap-2 px-2 text-sm">
          <input
            type="checkbox"
            checked={overdue}
            onChange={(e) => setOverdue(e.target.checked)}
            data-testid="repair-filter-overdue"
          />
          Overdue only
        </label>
        <label className="flex items-center gap-2 px-2 text-sm">
          <input
            type="checkbox"
            checked={allBranches}
            onChange={(e) => setAllBranches(e.target.checked)}
            data-testid="repair-filter-all-branches"
          />
          Both stores
        </label>
        <div className="relative ml-auto">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            className="h-10 w-[240px] rounded-none pl-9"
            placeholder="Phone, name or R number"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="repair-search-input"
          />
        </div>
      </div>

      <div className="mt-5 overflow-x-auto border border-border">
        <table className="w-full min-w-[900px] text-sm" data-testid="repairs-table">
          <thead className="bg-[#0A0A0A] text-white">
            <tr>
              {["Repair", "Booked", "Customer", "Item", "Promised", "Status", "Owing"].map((h) => (
                <th
                  key={h}
                  className={`px-3 py-2.5 font-semibold ${
                    h === "Owing" ? "text-right" : "text-left"
                  }`}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-12 text-center text-muted-foreground">
                  {loading ? "Loading…" : "No repairs match this view."}
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const late =
                r.promised_date &&
                r.promised_date < today &&
                !["collected", "cancelled"].includes(r.status);
              return (
                <tr key={r.id} className="hover:bg-[#FBF8F2]" data-testid={`repair-row-${r.repair_number}`}>
                  <td className="px-3 py-2.5 font-semibold">
                    <Link to={`/repairs/${r.id}`} className="hover:underline">
                      R{r.repair_number}
                    </Link>
                  </td>
                  <td className="tabular px-3 py-2.5 text-xs text-muted-foreground">
                    {shopDateTime(r.created_at)}
                  </td>
                  <td className="px-3 py-2.5">
                    {r.customer_name}
                    <div className="tabular text-xs text-muted-foreground">{formatPhone(r.customer_phone)}</div>
                  </td>
                  <td className="max-w-[260px] truncate px-3 py-2.5">{r.item_description}</td>
                  <td className="tabular px-3 py-2.5">
                    {r.promised_date || "-"}
                    {late && (
                      <span className="ml-2 text-xs font-bold uppercase text-[#9A3412]">late</span>
                    )}
                  </td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`px-2 py-1 text-[10px] font-bold uppercase ${
                        REPAIR_STATUS[r.status]?.cls || "bg-muted"
                      }`}
                      data-testid={`repair-status-${r.repair_number}`}
                    >
                      {REPAIR_STATUS[r.status]?.label || r.status}
                    </span>
                  </td>
                  <td className="tabular px-3 py-2.5 text-right font-semibold">
                    {r.balance > 0.005 ? money(r.balance) : "-"}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && (
        <p className="mt-3 flex items-center gap-2 text-xs text-muted-foreground">
          <Wrench className="h-3.5 w-3.5" /> {rows.length} job
          {rows.length === 1 ? "" : "s"} shown
        </p>
      )}
    </div>
  );
}
